"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { useTrack } from "@/hooks/useTrack";
import { installUsageTracker } from "@/lib/analytics/track";
import { WORKSPACE_ROUTE_TEMPLATES } from "@/config/workspace-routes";

// Never emit page views for dev/preview tooling or pre-auth surfaces.
const SKIPPED_PREFIXES = ["/preview", "/dev", "/design-system", "/login", "/oauth"];

// Workspace sections are the tail segment of `/{slug}/{section}` templates
// (ideas, tasks, goals, …). The same names appear slug-less on the top-level
// redirect routes that resolve the user's default namespace.
const WORKSPACE_SECTIONS = new Set(
  Object.values(WORKSPACE_ROUTE_TEMPLATES).map((template) => template.split("/")[2]),
);

// Non-workspace routes worth naming (see config/navigation.ts).
const TOP_LEVEL_SECTIONS = new Set(["onboarding", "chat", "command-log"]);

type PageSection = { section: string; slugScoped: boolean };

/**
 * Map a pathname to a telemetry-safe section name. Sections are enum values
 * derived from route config — never the raw path, which may embed a
 * user-authored namespace slug. Unknown routes collapse to "other".
 */
function resolvePageSection(pathname: string): PageSection | null {
  if (SKIPPED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return null;

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return { section: "home", slugScoped: false };
  if (segments.length >= 2 && WORKSPACE_SECTIONS.has(segments[1])) {
    return { section: segments[1], slugScoped: true };
  }
  if (WORKSPACE_SECTIONS.has(segments[0]) || TOP_LEVEL_SECTIONS.has(segments[0])) {
    return { section: segments[0], slugScoped: false };
  }
  return { section: "other", slugScoped: false };
}

/**
 * Mounts the usage tracker once for the whole app: starts periodic flushing
 * with a final sendBeacon flush, keeps the consent/namespace context synced
 * (via useTrack), and emits `page.viewed` on every pathname change. Renders
 * nothing.
 */
export function UsageTracker() {
  const track = useTrack();
  const pathname = usePathname();
  const lastTrackedPathRef = useRef<string | null>(null);

  useEffect(() => installUsageTracker(), []);

  useEffect(() => {
    if (!pathname || pathname === lastTrackedPathRef.current) return;
    lastTrackedPathRef.current = pathname;

    const page = resolvePageSection(pathname);
    if (!page) return;
    track("page.viewed", { section: page.section, slug_scoped: page.slugScoped });
  }, [pathname, track]);

  return null;
}
